import React, {useContext, useEffect, useState} from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    FormControlLabel,
    Grid,
    IconButton,
    MenuItem,
    Paper,
    Select,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {DatePicker} from '@mui/x-date-pickers/DatePicker';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, {Dayjs} from 'dayjs';
import 'dayjs/locale/de';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import APIWrapperContext from '../../../../services/APIWrapperContext';
import {formatInvoiceNumber, Invoice, InvoiceCreate, InvoiceDetail, InvoiceEmitter, InvoiceLineItemCreate, InvoiceProduct, InvoiceService,} from '../../../../services/invoiceService';
import type {VatBreakdown, XRechnungInvoiceData, XRechnungLineItem, XRechnungOptions} from '../Tasks/utils/xrechnung';
import {DEFAULT_PAYMENT_DAYS, XRECHNUNG_SPEC_ID,} from '../Tasks/utils/xrechnung';
import {createInvoice, InvoiceData, InvoiceLineItem as InvoiceGeneratorLineItem} from '../Tasks/utils/invoiceGenerator';
import {TasksService} from '../../../../services/openapi/services/TasksService';
import {ArbeitszeitService} from '../../../../services/openapi/services/ArbeitszeitService';
import type {Contact} from '../../../../services/openapi/models/Contact';
import type {BauvorhabenWithID} from '../../../../services/openapi/models/BauvorhabenWithID';

interface LineItemLocal {
    id: string; // temp id for local state
    productId?: number; // selected product ID, undefined = custom
    productName: string; // product name or custom name
    description: string; // detailed description (textarea)
    quantity: string;
    unitCode: string;
    unitLabel: string;
    unitPrice: string;
    vatRate: string;
}

// Task data that can be used to prefill invoice
export interface TaskDataForInvoice {
    // Invoice recipient fields
    invoice_name?: string;
    invoice_email?: string;
    invoice_phone?: string;
    invoice_street?: string;
    invoice_additional_street?: string;
    invoice_postal_code?: string;
    invoice_city?: string;
    // Full contact fields (textarea mode)
    invoice_full_contact?: string;
    invoice_use_full_contact?: boolean;
    // Rapport data for positions
    arbeitszeit?: number;
    anfahrt_abfahrt_mit_kfz?: boolean;
    material?: string;
    // Task info
    title?: string;
    description?: string;
    rapport?: string;
    projekt_name?: string;
    // Work date (from rapport upload/close timestamp)
    workDate?: string;
}

interface InvoiceCreateDialogProps {
    open: boolean;
    emitter: InvoiceEmitter;
    onClose: () => void;
    onSave: () => void;
    onChangeEmitter: () => void;
    taskData?: TaskDataForInvoice;
    taskId?: number;
}

// Unit codes for dropdown
const UNIT_OPTIONS = [
    {code: 'HUR', label: 'Std.', name: 'Stunde'},
    {code: 'C62', label: 'Stk.', name: 'Stück'},
    {code: 'KGM', label: 'kg', name: 'Kilogramm'},
    {code: 'MTR', label: 'm', name: 'Meter'},
    {code: 'LTR', label: 'l', name: 'Liter'},
    {code: 'MTK', label: 'm²', name: 'Quadratmeter'},
    {code: 'DAY', label: 'Tag', name: 'Tag'},
];

// Format currency
const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR',
    }).format(amount);
};

// Parse German number format
const parseNumber = (value: string): number => {
    const cleaned = value.replace(',', '.');
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? 0 : parsed;
};

// Generate unique ID for line items
const generateId = (): string => {
    return Math.random().toString(36).substring(2, 11);
};

const InvoiceCreateDialog: React.FC<InvoiceCreateDialogProps> = ({
                                                                     open,
                                                                     emitter,
                                                                     onClose,
                                                                     onSave,
                                                                     onChangeEmitter,
                                                                     taskData,
                                                                     taskId,
                                                                 }) => {
    const {call_backend_api} = useContext(APIWrapperContext);
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [finalizing, setFinalizing] = useState(false);
    const [showFinalizeConfirm, setShowFinalizeConfirm] = useState(false);

    // Buyer fields
    const [buyerName, setBuyerName] = useState('');
    const [buyerStreet, setBuyerStreet] = useState('');
    const [buyerPostalCode, setBuyerPostalCode] = useState('');
    const [buyerCity, setBuyerCity] = useState('');
    const [buyerCountryCode, setBuyerCountryCode] = useState('DE');
    const [buyerContactEmail, setBuyerContactEmail] = useState('');
    const [buyerFullContact, setBuyerFullContact] = useState('');
    const [buyerUseFullContact, setBuyerUseFullContact] = useState(true);

    // Invoice details
    const [invoiceNumber, setInvoiceNumber] = useState('');
    const [objekt, setObjekt] = useState('');
    const [workDescription, setWorkDescription] = useState('');
    const [workDate, setWorkDate] = useState<Dayjs | null>(null);
    const [note, setNote] = useState('');

    // Skonto (Cash Discount) and Payment Terms
    const [skontoPercentage, setSkontoPercentage] = useState('');
    const [skontoDays, setSkontoDays] = useState('');
    const [paymentDays, setPaymentDays] = useState('30');

    // Line items
    const [lineItems, setLineItems] = useState<LineItemLocal[]>([]);

    // Products for quick-add
    const [products, setProducts] = useState<InvoiceProduct[]>([]);

    // Contacts for autocomplete
    const [contacts, setContacts] = useState<Contact[]>([]);

    // Bauvorhaben for objekt autocomplete
    const [bauvorhaben, setBauvorhaben] = useState<BauvorhabenWithID[]>([]);

    // Highlight validation errors on hover
    const [highlightErrors, setHighlightErrors] = useState(false);

    // Clear error highlighting when form becomes valid
    useEffect(() => {
        if (highlightErrors && canFinalize().valid) {
            setHighlightErrors(false);
        }
    }, [buyerName, buyerStreet, buyerPostalCode, buyerCity, lineItems, highlightErrors]);

    // Helper function to find product by name (case-insensitive partial match)
    const findProductByName = (productList: InvoiceProduct[], searchName: string): InvoiceProduct | undefined => {
        const searchLower = searchName.toLowerCase();
        return productList.find(p => p.name.toLowerCase().includes(searchLower));
    };

    // Create line items from task data with product matching
    const createLineItemsFromTaskData = (productList: InvoiceProduct[]) => {
        if (!taskData) return;

        const initialLineItems: LineItemLocal[] = [];

        // Add arbeitszeit as a position if set - match to "Arbeitsstunden" product
        if (taskData.arbeitszeit && taskData.arbeitszeit > 0) {
            const product = findProductByName(productList, 'Arbeitsstunden');
            initialLineItems.push({
                id: generateId(),
                productId: product?.id,
                productName: product?.name || 'Arbeitsstunden',
                description: '',
                quantity: taskData.arbeitszeit.toString().replace('.', ','),
                unitCode: product?.unit_code || 'HUR',
                unitLabel: product?.unit_label || 'Std.',
                unitPrice: product?.default_price?.toString().replace('.', ',') || '',
                vatRate: product?.vat_rate?.toString() || '19',
            });
        }

        // Add An-/Abfahrt as a position if enabled - match to "Anfahrt" product
        if (taskData.anfahrt_abfahrt_mit_kfz) {
            const product = findProductByName(productList, 'An- und Abfahrt');
            initialLineItems.push({
                id: generateId(),
                productId: product?.id,
                productName: product?.name || 'An-/Abfahrt',
                description: product?.description || '',
                quantity: '1',
                unitCode: product?.unit_code || 'C62',
                unitLabel: product?.unit_label || 'Stk.',
                unitPrice: product?.default_price?.toString().replace('.', ',') || '',
                vatRate: product?.vat_rate?.toString() || '19',
            });
        }

        // Add material as a position if set - match to "Material" product
        if (taskData.material && taskData.material.trim()) {
            const product = findProductByName(productList, 'Material');
            initialLineItems.push({
                id: generateId(),
                productId: product?.id,
                productName: product?.name || 'Material',
                description: taskData.material.trim(),
                quantity: '1',
                unitCode: product?.unit_code || 'C62',
                unitLabel: product?.unit_label || 'Stk.',
                unitPrice: product?.default_price?.toString().replace('.', ',') || '',
                vatRate: product?.vat_rate?.toString() || '19',
            });
        }

        setLineItems(initialLineItems);
    };

    useEffect(() => {
        if (open) {
            // Reset form with task data if available
            if (taskData) {
                console.log(taskData);
                console.log(taskData);
                console.log(taskData);
                // Check if task uses full contact mode
                const useFullContact = taskData.invoice_use_full_contact !== false;
                setBuyerUseFullContact(useFullContact);

                if (useFullContact && taskData.invoice_full_contact) {
                    // Use full contact textarea
                    setBuyerFullContact(taskData.invoice_full_contact);
                    // Also set individual fields for fallback
                    setBuyerName(taskData.invoice_name || '');
                    setBuyerStreet(taskData.invoice_street || '');
                    setBuyerPostalCode(taskData.invoice_postal_code || '');
                    setBuyerCity(taskData.invoice_city || '');
                    setBuyerContactEmail(taskData.invoice_email || '');
                } else {
                    // Use structured fields
                    setBuyerName(taskData.invoice_name || '');
                    setBuyerStreet(taskData.invoice_street || '');
                    setBuyerPostalCode(taskData.invoice_postal_code || '');
                    setBuyerCity(taskData.invoice_city || '');
                    setBuyerContactEmail(taskData.invoice_email || '');
                    setBuyerFullContact('');
                }
                setBuyerCountryCode('DE');

                // Prefill work description from rapport text (or fallback to title)
                setWorkDescription(taskData.rapport || taskData.title || '');

                // Set work date from rapport timestamp (date only)
                if (taskData.workDate) {
                    const dateOnly = dayjs(taskData.workDate).startOf('day');
                    setWorkDate(dateOnly);
                } else {
                    setWorkDate(null);
                }
            } else {
                // No task data - reset to empty
                setBuyerName('');
                setBuyerStreet('');
                setBuyerPostalCode('');
                setBuyerCity('');
                setBuyerCountryCode('DE');
                setBuyerContactEmail('');
                setBuyerFullContact('');
                setBuyerUseFullContact(true);
                setWorkDescription('');
                setLineItems([]);
                setWorkDate(null);
                setObjekt('');
                setInvoiceNumber('');
            }

            // Set objekt from projekt_name if available
            if (taskData?.projekt_name) {
                setObjekt(taskData.projekt_name);
            }

            setNote('');
            setError(null);

            // Load products first, then create line items with product matching
            loadProducts();
            // Load contacts for autocomplete
            loadContacts();
            // Load bauvorhaben for objekt autocomplete
            loadBauvorhaben();
        }
    }, [open, emitter, taskData]);

    const loadProducts = () => {
        call_backend_api(
            InvoiceService.getProducts(true),
            (data: InvoiceProduct[]) => {
                setProducts(data);
                // After products are loaded, create line items with product matching
                if (taskData) {
                    createLineItemsFromTaskData(data);
                }
            },
            (err: any) => {
                console.error('Error loading products:', err);
                // Still create line items without product matching on error
                if (taskData) {
                    createLineItemsFromTaskData([]);
                }
            },
            undefined,
            false
        );
    };

    const loadContacts = () => {
        call_backend_api(
            TasksService.getAllContactsTaskContactsAllGet(),
            (data: Contact[]) => {
                setContacts(data);
            },
            (err: any) => {
                console.error('Error loading contacts:', err);
            },
            undefined,
            false
        );
    };

    const loadBauvorhaben = () => {
        call_backend_api(
            ArbeitszeitService.getAlleBauvorhabenArbeitzeitBauvorhabenGet(false),
            (data: BauvorhabenWithID[]) => {
                setBauvorhaben(data);
            },
            (err: any) => {
                console.error('Error loading bauvorhaben:', err);
            },
            undefined,
            false
        );
    };

    // Format contact as multi-line text for textarea
    const formatContactAsText = (contact: Contact): string => {
        const lines: string[] = [];
        if (contact.name) lines.push(contact.name);
        if (contact.email) lines.push(contact.email);
        if (contact.phone) lines.push(contact.phone);
        if (contact.address_text) lines.push(contact.address_text);
        return lines.join('\n');
    };

    // Handle contact selection for textarea mode (full contact)
    const handleContactSelectForTextarea = (contact: Contact | null) => {
        if (contact) {
            setBuyerFullContact(formatContactAsText(contact));
            // Also set individual fields as backup
            setBuyerName(contact.name || '');
            setBuyerContactEmail(contact.email || '');
        }
    };

    // Handle contact selection from autocomplete (structured mode)
    const handleContactSelect = (contact: Contact | null) => {
        if (contact) {
            setBuyerName(contact.name || '');
            setBuyerContactEmail(contact.email || '');
            // Parse address_text if available - assume format: "Street, PLZ City"
            if (contact.address_text) {
                const parts = contact.address_text.split(',').map(p => p.trim());
                if (parts.length >= 2) {
                    setBuyerStreet(parts[0]);
                    // Try to parse PLZ and city from second part
                    const plzCityMatch = parts[1].match(/^(\d{5})\s*(.*)$/);
                    if (plzCityMatch) {
                        setBuyerPostalCode(plzCityMatch[1]);
                        setBuyerCity(plzCityMatch[2]);
                    } else {
                        setBuyerCity(parts[1]);
                    }
                } else {
                    setBuyerStreet(contact.address_text);
                }
            }
        }
    };

    // Calculate totals
    const calculateLineTotal = (item: LineItemLocal): number => {
        const qty = parseNumber(item.quantity);
        const price = parseNumber(item.unitPrice);
        return qty * price;
    };

    const subtotal = lineItems.reduce((sum, item) => sum + calculateLineTotal(item), 0);

    // Group VAT by rate
    const vatGroups = lineItems.reduce((groups, item) => {
        const rate = parseNumber(item.vatRate);
        const lineTotal = calculateLineTotal(item);
        if (!groups[rate]) {
            groups[rate] = 0;
        }
        groups[rate] += lineTotal;
        return groups;
    }, {} as Record<number, number>);

    const totalVat = Object.entries(vatGroups).reduce((sum, [rate, amount]) => {
        return sum + (amount * parseFloat(rate) / 100);
    }, 0);

    const total = subtotal + totalVat;

    // Add new line item
    const handleAddLineItem = () => {
        setLineItems([
            ...lineItems,
            {
                id: generateId(),
                productId: undefined,
                productName: '',
                description: '',
                quantity: '1',
                unitCode: 'HUR',
                unitLabel: 'Std.',
                unitPrice: '',
                vatRate: '19',
            },
        ]);
    };

    // Handle product selection for a line item (Autocomplete)
    const handleProductChange = (id: string, value: InvoiceProduct | string | null) => {
        setLineItems(items =>
            items.map(item => {
                if (item.id === id) {
                    if (value === null) {
                        // Cleared - reset to empty
                        return {
                            ...item,
                            productId: undefined,
                            productName: '',
                        };
                    } else if (typeof value === 'string') {
                        // Free text entry
                        return {
                            ...item,
                            productId: undefined,
                            productName: value,
                        };
                    } else {
                        // Product selected
                        const unit = UNIT_OPTIONS.find(u => u.code === value.unit_code) || UNIT_OPTIONS[0];
                        return {
                            ...item,
                            productId: value.id,
                            productName: value.name,
                            description: value.description || '',
                            unitCode: value.unit_code,
                            unitLabel: unit.label,
                            unitPrice: value.default_price?.toString().replace('.', ',') || '',
                            vatRate: value.vat_rate.toString(),
                        };
                    }
                }
                return item;
            })
        );
    };

    // Update line item
    const handleUpdateLineItem = (id: string, field: keyof LineItemLocal, value: string) => {
        setLineItems(items =>
            items.map(item => {
                if (item.id === id) {
                    const updated = {...item, [field]: value};
                    // Update unit label when unit code changes
                    if (field === 'unitCode') {
                        const unit = UNIT_OPTIONS.find(u => u.code === value);
                        if (unit) {
                            updated.unitLabel = unit.label;
                        }
                    }
                    return updated;
                }
                return item;
            })
        );
    };

    // Remove line item
    const handleRemoveLineItem = (id: string) => {
        setLineItems(items => items.filter(item => item.id !== id));
    };

    // Check if invoice can be finalized (all required fields filled)
    const canFinalize = (): { valid: boolean; errors: string[] } => {
        const errors: string[] = [];

        // Invoice number is required for finalization
        const invoiceNum = parseInt(invoiceNumber, 10);
        if (!invoiceNumber.trim() || isNaN(invoiceNum) || invoiceNum < 1) {
            errors.push('Rechnungsnummer ist erforderlich');
        }

        if (buyerUseFullContact) {
            // In full contact mode, just need some contact text
            if (!buyerFullContact.trim()) {
                errors.push('Kundenadresse ist erforderlich');
            }
        } else {
            // In structured mode, need individual fields
            if (!buyerName.trim()) {
                errors.push('Kundenname ist erforderlich');
            }
            if (!buyerStreet.trim()) {
                errors.push('Straße ist erforderlich');
            }
            if (!buyerPostalCode.trim()) {
                errors.push('PLZ ist erforderlich');
            }
            if (!buyerCity.trim()) {
                errors.push('Stadt ist erforderlich');
            }
        }

        if (lineItems.length === 0) {
            errors.push('Mindestens eine Position ist erforderlich');
        }

        for (let i = 0; i < lineItems.length; i++) {
            const item = lineItems[i];
            if (!item.productName.trim()) {
                errors.push(`Position ${i + 1}: Produkt/Bezeichnung ist erforderlich`);
            }
            if (parseNumber(item.quantity) <= 0) {
                errors.push(`Position ${i + 1}: Menge muss größer als 0 sein`);
            }
            if (parseNumber(item.unitPrice) <= 0) {
                errors.push(`Position ${i + 1}: Preis muss größer als 0 sein`);
            }
        }

        return {valid: errors.length === 0, errors};
    };

    // Build invoice data for API
    const buildInvoiceData = (): InvoiceCreate => {
        const apiLineItems: InvoiceLineItemCreate[] = lineItems.map((item, index) => ({
            line_number: index + 1,
            description: item.productName.trim(),
            note: item.description.trim() || undefined,
            quantity: parseNumber(item.quantity),
            unit_code: item.unitCode,
            unit_price: parseNumber(item.unitPrice),
            vat_rate: parseNumber(item.vatRate),
            product_id: item.productId,
        }));

        return {
            emitter_id: emitter.id,
            invoice_number: invoiceNumber.trim() ? parseInt(invoiceNumber, 10) : undefined,
            buyer_name: buyerUseFullContact ? undefined : (buyerName.trim() || undefined),
            buyer_street: buyerUseFullContact ? undefined : (buyerStreet.trim() || undefined),
            buyer_postal_code: buyerUseFullContact ? undefined : (buyerPostalCode.trim() || undefined),
            buyer_city: buyerUseFullContact ? undefined : (buyerCity.trim() || undefined),
            buyer_country_code: buyerUseFullContact ? undefined : (buyerCountryCode.trim() || undefined),
            buyer_contact_email: buyerUseFullContact ? undefined : (buyerContactEmail.trim() || undefined),
            buyer_full_contact: buyerUseFullContact ? (buyerFullContact.trim() || undefined) : undefined,
            objekt: objekt.trim() || undefined,
            work_description: workDescription.trim() || undefined,
            work_date: workDate ? workDate.format('YYYY-MM-DD') : undefined,
            note: note.trim() || undefined,
            skonto_percentage: skontoPercentage ? parseNumber(skontoPercentage) : undefined,
            skonto_days: skontoDays ? parseInt(skontoDays, 10) : undefined,
            payment_days: paymentDays ? parseInt(paymentDays, 10) : 30,
            line_items: apiLineItems,
            task_id: taskId,
        };
    };

    // Mark task as billed
    const markTaskAsBilled = () => {
        if (!taskId) return;

        call_backend_api(
            TasksService.updateTaskTaskTaskIdPut(taskId, {billing_status: 'BILLED'} as any),
            () => {
                console.log('Task marked as billed');
            },
            (err: any) => {
                console.warn('Could not mark task as billed:', err);
            },
            undefined,
            false
        );
    };

    // Save as draft (no strict validation)
    const handleSaveDraft = () => {
        setSaving(true);
        setError(null);

        const invoiceData = buildInvoiceData();

        call_backend_api(
            InvoiceService.createInvoice(invoiceData),
            () => {
                setSaving(false);
                onSave();
            },
            (err: any) => {
                console.error('Error creating invoice:', err);
                setError('Fehler beim Erstellen der Rechnung');
                setSaving(false);
            },
            undefined,
            false
        );
    };

    // Request finalization (show confirmation dialog)
    const handleFinalize = () => {
        const validation = canFinalize();
        if (!validation.valid) {
            setError(validation.errors.join('\n'));
            return;
        }
        setShowFinalizeConfirm(true);
    };

    // Generate PDF preview with "Entwurf" as invoice number
    const handlePreviewPdf = async () => {
        try {
            // Map current form data to line items
            const mappedLineItems: InvoiceGeneratorLineItem[] = lineItems.map(item => ({
                description: item.productName,
                note: item.description || undefined,
                quantity: parseNumber(item.quantity),
                unit: item.unitCode === 'HUR' ? 'Std.' : item.unitCode === 'C62' ? 'Stk.' : item.unitCode,
                unitPrice: parseNumber(item.unitPrice),
                total: parseNumber(item.quantity) * parseNumber(item.unitPrice),
            }));

            const invoiceData: InvoiceData = {
                invoiceNumber: 'Entwurf',
                invoiceDate: new Date(),
                customer: {
                    name: buyerName,
                    street: buyerStreet,
                    zipCity: `${buyerPostalCode} ${buyerCity}`.trim(),
                    fullContact: buyerUseFullContact ? buyerFullContact : undefined
                },
                emitter: emitter ? {
                    name: emitter.name,
                    street: emitter.street,
                    postal_code: emitter.postal_code,
                    city: emitter.city,
                    country_code: emitter.country_code,
                    contact_phone: emitter.contact_phone,
                    contact_email: emitter.contact_email,
                    website: emitter.website,
                    fax: emitter.fax,
                    management: emitter.management,
                    court_registration: emitter.court_registration,
                    po_box: emitter.po_box,
                    logo_path: emitter.logo_path,
                    iban: emitter.iban,
                    bic: emitter.bic,
                    bank_name: emitter.bank_name,
                    tax_number: emitter.tax_number,
                } : undefined,
                objekt: objekt,
                durchgefuehrteArbeiten: workDescription,
                durchgefuehrtAm: workDate ? workDate.toDate() : new Date(),
                lineItems: mappedLineItems,
                vatRate: 19,
                skonto_percentage: skontoPercentage ? parseNumber(skontoPercentage) : undefined,
                skonto_days: skontoDays ? parseInt(skontoDays, 10) : undefined,
                payment_days: paymentDays ? parseInt(paymentDays, 10) : 30,
            };

            // Generate PDF without XRechnung (preview only)
            const result = await createInvoice(invoiceData);

            // Open in new tab
            const url = URL.createObjectURL(result.pdfBlob);
            window.open(url, '_blank');
            // Clean up after a delay to allow the tab to load
            setTimeout(() => URL.revokeObjectURL(url), 5000);
        } catch (err) {
            console.error('Error generating preview PDF:', err);
            setError('Fehler beim Erstellen der PDF-Vorschau');
        }
    };

    // Build XRechnung data from invoice detail
    const buildXRechnungData = (invoiceDetail: InvoiceDetail): XRechnungInvoiceData => {
        const invoiceNumber = formatInvoiceNumber(invoiceDetail.invoice_number, invoiceDetail.invoice_date, emitter.short_key);
        const invoiceDate = invoiceDetail.invoice_date ? new Date(invoiceDetail.invoice_date) : new Date();
        const dueDate = invoiceDetail.due_date ? new Date(invoiceDetail.due_date) : new Date(Date.now() + DEFAULT_PAYMENT_DAYS * 24 * 60 * 60 * 1000);

        const xLineItems: XRechnungLineItem[] = invoiceDetail.line_items.map((item, idx) => ({
            id: (idx + 1).toString(),
            name: item.description,  // BT-153: Item name (required)
            note: item.note,         // BT-154: Item description (optional)
            quantity: item.quantity,
            unitCode: item.unit_code,
            unitPrice: item.unit_price,
            lineNetAmount: item.line_net_amount,
            vatCategoryCode: item.vat_category_code || 'S',
            vatRate: item.vat_rate,
        }));

        const xVatBreakdown: VatBreakdown[] = invoiceDetail.vat_breakdown.map(vat => ({
            categoryCode: vat.vat_category_code || 'S',
            rate: vat.vat_rate,
            taxableAmount: vat.taxable_amount,
            taxAmount: vat.tax_amount,
        }));

        return {
            invoiceNumber,
            invoiceDate,
            invoiceTypeCode: '380',
            currencyCode: 'EUR',
            buyerReference: invoiceDetail.buyer_reference || invoiceNumber,
            note: invoiceDetail.note,
            specificationId: XRECHNUNG_SPEC_ID,
            seller: {
                name: emitter.name,
                registrationName: emitter.name,
                identification: {
                    vatId: emitter.vat_id || '',
                    taxNumber: emitter.tax_number,
                },
                postalAddress: {
                    streetName: emitter.street,
                    additionalStreetName: emitter.additional_street,
                    cityName: emitter.city,
                    postalZone: emitter.postal_code,
                    countryCode: emitter.country_code || 'DE',
                },
                contact: {
                    name: emitter.contact_name,
                    telephone: emitter.contact_phone,
                    email: emitter.contact_email,
                },
                companyId: emitter.company_id,
            },
            buyer: {
                name: invoiceDetail.buyer_name || '',
                postalAddress: {
                    streetName: invoiceDetail.buyer_street || '',
                    additionalStreetName: invoiceDetail.buyer_additional_street,
                    cityName: invoiceDetail.buyer_city || '',
                    postalZone: invoiceDetail.buyer_postal_code || '',
                    countryCode: invoiceDetail.buyer_country_code || 'DE',
                },
                contact: {
                    email: invoiceDetail.buyer_contact_email,
                    telephone: invoiceDetail.buyer_contact_phone,
                    name: invoiceDetail.buyer_contact_name,
                },
            },
            deliveryDate: invoiceDetail.work_date ? new Date(invoiceDetail.work_date) : undefined,
            deliveryLocation: invoiceDetail.objekt,
            deliveryAddress: {
                streetName: invoiceDetail.objekt || invoiceDetail.buyer_street || '',
                cityName: invoiceDetail.buyer_city || '',
                postalZone: invoiceDetail.buyer_postal_code || '',
                countryCode: invoiceDetail.buyer_country_code || 'DE',
            },
            paymentMeans: {
                paymentMeansCode: '58',
                iban: emitter.iban,
                bic: emitter.bic,
                accountName: emitter.account_holder || emitter.name,
            },
            paymentTerms: {
                dueDate,
                note: `Zahlbar innerhalb von ${DEFAULT_PAYMENT_DAYS} Tagen ohne Abzug.`,
            },
            lineItems: xLineItems,
            lineExtensionAmount: invoiceDetail.line_extension_amount || 0,
            taxExclusiveAmount: invoiceDetail.tax_exclusive_amount || 0,
            taxInclusiveAmount: invoiceDetail.tax_inclusive_amount || 0,
            payableAmount: invoiceDetail.payable_amount || 0,
            vatBreakdown: xVatBreakdown,
            objekt: invoiceDetail.objekt,
            durchgefuehrteArbeiten: invoiceDetail.work_description,
            durchgefuehrtAm: invoiceDetail.work_date ? new Date(invoiceDetail.work_date) : undefined,
        };
    };

    // Map InvoiceDetail to InvoiceData for invoiceGenerator
    const mapInvoiceDetailToInvoiceData = (invoiceDetail: InvoiceDetail): InvoiceData => {
        const invoiceNumber = formatInvoiceNumber(invoiceDetail.invoice_number, invoiceDetail.invoice_date, emitter.short_key);

        // Map line items
        const lineItems: InvoiceGeneratorLineItem[] = invoiceDetail.line_items.map(item => ({
            description: item.description,
            note: item.note || undefined,
            quantity: item.quantity,
            unit: item.unit_code === 'HUR' ? 'Std.' : item.unit_code === 'C62' ? 'Stk.' : item.unit_code,
            unitPrice: item.unit_price,
            total: item.line_net_amount,
        }));

        return {
            invoiceNumber,
            invoiceDate: invoiceDetail.invoice_date ? new Date(invoiceDetail.invoice_date) : new Date(),
            customer: {
                name: invoiceDetail.buyer_name || '',
                street: invoiceDetail.buyer_street || '',
                zipCity: `${invoiceDetail.buyer_postal_code || ''} ${invoiceDetail.buyer_city || ''}`.trim(),
                fullContact: invoiceDetail.buyer_full_contact ? invoiceDetail.buyer_full_contact : undefined
            },
            emitter: {
                name: emitter.name,
                street: emitter.street,
                postal_code: emitter.postal_code,
                city: emitter.city,
                country_code: emitter.country_code,
                contact_phone: emitter.contact_phone,
                contact_email: emitter.contact_email,
                website: emitter.website,
                fax: emitter.fax,
                management: emitter.management,
                court_registration: emitter.court_registration,
                po_box: emitter.po_box,
                logo_path: emitter.logo_path,
                iban: emitter.iban,
                bic: emitter.bic,
                bank_name: emitter.bank_name,
                tax_number: emitter.tax_number,
            },
            objekt: invoiceDetail.objekt || '',
            durchgefuehrteArbeiten: invoiceDetail.work_description || '',
            durchgefuehrtAm: invoiceDetail.work_date ? new Date(invoiceDetail.work_date) : new Date(),
            lineItems,
            vatRate: 19, // Default VAT rate
            skonto_percentage: invoiceDetail.skonto_percentage,
            skonto_days: invoiceDetail.skonto_days,
            payment_days: invoiceDetail.payment_days ?? 30,
        };
    };

    // Generate PDF with embedded XRechnung XML and upload to backend
    const generateInvoicePdf = async (invoiceDetail: InvoiceDetail): Promise<void> => {
        try {
            // Map invoice detail to InvoiceData format
            const invoiceData = mapInvoiceDetailToInvoiceData(invoiceDetail);

            // Build XRechnung options with buyer data
            const xrechnungOptions: XRechnungOptions = {
                generateXml: true,
                embedInPdf: true,
                buyerData: {
                    name: invoiceDetail.buyer_name,
                    postalAddress: {
                        streetName: invoiceDetail.buyer_street || '',
                        cityName: invoiceDetail.buyer_city || '',
                        postalZone: invoiceDetail.buyer_postal_code || '',
                        countryCode: invoiceDetail.buyer_country_code || 'DE',
                    },
                    contact: {
                        email: invoiceDetail.buyer_contact_email,
                        telephone: invoiceDetail.buyer_contact_phone,
                        name: invoiceDetail.buyer_contact_name,
                    },
                },
                buyerReference: invoiceDetail.buyer_reference || invoiceData.invoiceNumber,
            };

            // Generate PDF using invoiceGenerator
            const result = await createInvoice(invoiceData, xrechnungOptions);

            // Create file for upload
            const file = new File([result.pdfBlob], result.fileName, {type: 'application/pdf'});

            // Upload PDF to backend
            await new Promise<void>((resolve, reject) => {
                call_backend_api(
                    InvoiceService.uploadInvoicePdf(invoiceDetail.id, file),
                    () => {
                        console.log('Invoice PDF uploaded successfully');
                        resolve();
                    },
                    (err: any) => {
                        console.error('Error uploading invoice PDF:', err);
                        reject(err);
                    },
                    undefined,
                    false
                );
            });

            // Also download the PDF for the user
            const url = URL.createObjectURL(result.pdfBlob);
            const a = document.createElement('a');
            a.href = url;
            a.download = result.fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error generating PDF:', err);
            // Don't fail the finalization if PDF generation fails
        }
    };

    // Confirm and execute finalization
    const confirmFinalize = async () => {
        setShowFinalizeConfirm(false);
        setFinalizing(true);
        setError(null);

        const invoiceData = buildInvoiceData();
        // First create the invoice
        call_backend_api(
            InvoiceService.createInvoice(invoiceData),
            (createdInvoice: Invoice) => {
                // Then finalize it
                call_backend_api(
                    InvoiceService.finalizeInvoice(createdInvoice.id),
                    (finalizeResult: any) => {
                        // Get the full invoice details for PDF generation
                        call_backend_api(
                            InvoiceService.getInvoice(createdInvoice.id),
                            async (invoiceDetail: InvoiceDetail) => {
                                // Generate PDF with XRechnung XML
                                await generateInvoicePdf(invoiceDetail);
                                setFinalizing(false);
                                markTaskAsBilled();
                                onSave();
                            },
                            (err: any) => {
                                console.error('Error fetching invoice details:', err);
                                // Still complete successfully since invoice was finalized
                                setFinalizing(false);
                                markTaskAsBilled();
                                onSave();
                            },
                            undefined,
                            false
                        );
                    },
                    (err: any) => {
                        console.error('Error finalizing invoice:', err);
                        setError('Fehler beim Abschließen der Rechnung');
                        setFinalizing(false);
                    },
                    undefined,
                    false
                );
            },
            (err: any) => {
                console.error('Error creating invoice:', err);
                setError('Fehler beim Erstellen der Rechnung');
                setFinalizing(false);
            },
            undefined,
            false
        );
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="de">
            <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
                <DialogTitle>
                    <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                        <span>Neue Rechnung erstellen</span>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {error && (
                        <Alert severity="error" sx={{mb: 2, mt: 1}}>
                            {error}
                        </Alert>
                    )}

                    {/* Emitter Section */}
                    <Paper variant="outlined" sx={{p: 2, mb: 3, mt: 1, backgroundColor: 'grey.50'}}>
                        <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                            <Box>
                                <Typography variant="caption" color="text.secondary">
                                    Rechnungsaussteller
                                </Typography>
                                <Typography variant="body1" fontWeight="medium">
                                    {emitter.name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {emitter.street}, {emitter.postal_code} {emitter.city}
                                </Typography>
                            </Box>
                            <Button
                                size="small"
                                startIcon={<EditIcon/>}
                                onClick={onChangeEmitter}
                            >
                                Ändern
                            </Button>
                        </Box>
                    </Paper>

                    {/* Buyer Section */}
                    <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1}}>
                        <Typography variant="subtitle2" color="text.secondary">
                            KUNDENADRESSE
                        </Typography>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size='small'
                                    checked={!buyerUseFullContact}
                                    onChange={(e) => {
                                        const useStructured = e.target.checked;
                                        if (!useStructured && !buyerUseFullContact) {
                                            // Switching to textarea mode - auto-format existing data
                                            const parts = [
                                                buyerName,
                                                buyerContactEmail,
                                                buyerStreet,
                                                [buyerPostalCode, buyerCity].filter(Boolean).join(' ')
                                            ].filter(Boolean);
                                            setBuyerUseFullContact(true);
                                            setBuyerFullContact(parts.join('\n') || buyerFullContact);
                                        } else {
                                            setBuyerUseFullContact(!useStructured);
                                        }
                                    }}
                                />
                            }
                            label={<Typography variant='body2'>Einzelfelder</Typography>}
                            sx={{mr: 0}}
                        />
                    </Box>
                    {buyerUseFullContact ? (
                        <Box sx={{mb: 3}}>
                            <Autocomplete
                                options={contacts}
                                getOptionLabel={(option) => option.name}
                                onChange={(_, newValue) => handleContactSelectForTextarea(newValue)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Kontakt suchen"
                                        placeholder="Kontakt auswählen zum Ausfüllen..."
                                        size="small"
                                        sx={{mb: 1}}
                                    />
                                )}
                                renderOption={(props, option) => (
                                    <li {...props} key={option.id}>
                                        <Box>
                                            <Typography variant="body2">{option.name}</Typography>
                                            {option.email && (
                                                <Typography variant="caption" color="text.secondary">
                                                    {option.email}
                                                </Typography>
                                            )}
                                        </Box>
                                    </li>
                                )}
                            />
                            <TextField
                                label="Kontaktdaten"
                                value={buyerFullContact}
                                onChange={(e) => setBuyerFullContact(e.target.value)}
                                fullWidth
                                multiline
                                rows={5}
                                placeholder={'Name / Firma\nE-Mail\nStraße\nPLZ Stadt'}
                            />
                        </Box>
                    ) : (
                        <Grid container spacing={2} sx={{mb: 3}}>
                            <Grid item xs={12}>
                                <Autocomplete
                                    freeSolo
                                    options={contacts}
                                    getOptionLabel={(option) =>
                                        typeof option === 'string' ? option : option.name
                                    }
                                    value={contacts.find(c => c.name === buyerName) || buyerName}
                                    onChange={(_, newValue) => {
                                        if (newValue === null) {
                                            setBuyerName('');
                                        } else if (typeof newValue === 'string') {
                                            setBuyerName(newValue);
                                        } else {
                                            handleContactSelect(newValue);
                                        }
                                    }}
                                    onInputChange={(_, newInputValue, reason) => {
                                        if (reason === 'input') {
                                            setBuyerName(newInputValue);
                                        }
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Name / Firma"
                                            required
                                            error={highlightErrors && !buyerName.trim()}
                                            placeholder="Kontakt auswählen oder eingeben..."
                                        />
                                    )}
                                    renderOption={(props, option) => (
                                        <li {...props} key={option.id}>
                                            <Box>
                                                <Typography variant="body2">{option.name}</Typography>
                                                {option.email && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {option.email}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </li>
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label="Straße"
                                    value={buyerStreet}
                                    onChange={(e) => setBuyerStreet(e.target.value)}
                                    fullWidth
                                    error={highlightErrors && !buyerStreet.trim()}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    label="PLZ"
                                    value={buyerPostalCode}
                                    onChange={(e) => setBuyerPostalCode(e.target.value)}
                                    fullWidth
                                    error={highlightErrors && !buyerPostalCode.trim()}
                                />
                            </Grid>
                            <Grid item xs={12} sm={5}>
                                <TextField
                                    label="Stadt"
                                    value={buyerCity}
                                    onChange={(e) => setBuyerCity(e.target.value)}
                                    fullWidth
                                    error={highlightErrors && !buyerCity.trim()}
                                />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <TextField
                                    label="Land"
                                    value={buyerCountryCode}
                                    onChange={(e) => setBuyerCountryCode(e.target.value)}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="E-Mail"
                                    type="email"
                                    value={buyerContactEmail}
                                    onChange={(e) => setBuyerContactEmail(e.target.value)}
                                    fullWidth
                                    placeholder="kunde@beispiel.de"
                                />
                            </Grid>
                        </Grid>
                    )}

                    <Divider sx={{my: 2}}/>

                    {/* Invoice Details Section */}
                    <Typography variant="subtitle2" color="text.secondary" sx={{mb: 1}}>
                        RECHNUNGSDETAILS
                    </Typography>
                    <Grid container spacing={2} sx={{mb: 3}}>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                label="Rechnungsnummer"
                                value={invoiceNumber}
                                onChange={(e) => setInvoiceNumber(e.target.value)}
                                fullWidth
                                type="number"
                                required
                                error={highlightErrors && (!invoiceNumber.trim() || parseInt(invoiceNumber, 10) < 1)}
                                helperText="Erforderlich für Abschluss"
                                inputProps={{min: 1}}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Autocomplete
                                freeSolo
                                options={bauvorhaben}
                                getOptionLabel={(option) =>
                                    typeof option === 'string' ? option : option.bvh_name
                                }
                                value={bauvorhaben.find(b => b.bvh_name === objekt) || objekt}
                                onChange={(_, newValue) => {
                                    if (newValue === null) {
                                        setObjekt('');
                                    } else if (typeof newValue === 'string') {
                                        setObjekt(newValue);
                                    } else {
                                        setObjekt(newValue.bvh_name);
                                    }
                                }}
                                onInputChange={(_, newInputValue, reason) => {
                                    if (reason === 'input') {
                                        setObjekt(newInputValue);
                                    }
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Objekt"
                                        placeholder="Bauvorhaben auswählen oder eingeben..."
                                    />
                                )}
                                renderOption={(props, option) => (
                                    <li {...props} key={option.id}>
                                        <Typography variant="body2">{option.bvh_name}</Typography>
                                    </li>
                                )}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Durchgeführte Arbeiten"
                                value={workDescription}
                                onChange={(e) => setWorkDescription(e.target.value)}
                                fullWidth
                                multiline
                                rows={4}
                                placeholder="z.B. Austausch eines defekten Stellmotors"
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <DatePicker
                                label="Durchgeführt am"
                                value={workDate}
                                onChange={(newValue) => setWorkDate(newValue)}
                                slotProps={{
                                    textField: {fullWidth: true},
                                }}
                            />
                        </Grid>
                    </Grid>

                    <Divider sx={{my: 2}}/>

                    {/* Line Items Section */}
                    <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1}}>
                        <Typography variant="subtitle2" color="text.secondary">
                            POSITIONEN
                        </Typography>
                        <Button
                            size="small"
                            startIcon={<AddIcon/>}
                            onClick={handleAddLineItem}
                        >
                            Position hinzufügen
                        </Button>
                    </Box>

                    {lineItems.length === 0 ? (
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 4,
                                mb: 2,
                                textAlign: 'center',
                                borderColor: highlightErrors ? 'error.main' : undefined,
                                borderWidth: highlightErrors ? 2 : 1,
                            }}
                        >
                            <Typography variant="body2" color={highlightErrors ? 'error' : 'text.secondary'}>
                                Noch keine Positionen. Klicken Sie auf "Position hinzufügen".
                            </Typography>
                        </Paper>
                    ) : (
                        <Box sx={{mb: 2}}>
                            {lineItems.map((item, index) => (
                                <Paper key={item.id} variant="outlined" sx={{p: 2, mb: 1}}>
                                    <Box>
                                        {/* Mobile: Header row with position number, total, and delete */}
                                        <Box sx={{
                                            display: {xs: 'flex', md: 'none'},
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            mb: 1,
                                        }}>
                                            <Typography variant="body2" color="text.secondary">
                                                Position {index + 1}
                                            </Typography>
                                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                                <Typography variant="body2" fontWeight="medium">
                                                    {formatCurrency(calculateLineTotal(item))}
                                                </Typography>
                                                <Tooltip title="Entfernen">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleRemoveLineItem(item.id)}
                                                        color="error"
                                                    >
                                                        <DeleteIcon fontSize="small"/>
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </Box>

                                        {/* Desktop: Row 1: Name, Menge, Unit, Preis, VAT, Total, Delete */}
                                        <Box sx={{
                                            display: {xs: 'none', md: 'flex'},
                                            alignItems: 'center',
                                            gap: 1,
                                            mb: 1,
                                        }}>
                                            <Typography variant="body2" color="text.secondary" sx={{minWidth: 24}}>
                                                {index + 1}.
                                            </Typography>
                                            <Autocomplete
                                                freeSolo
                                                size="small"
                                                options={products}
                                                getOptionLabel={(option) =>
                                                    typeof option === 'string' ? option : option.name
                                                }
                                                value={item.productId ? products.find(p => p.id === item.productId) || null : item.productName}
                                                onChange={(_, newValue) => handleProductChange(item.id, newValue)}
                                                onInputChange={(_, newInputValue, reason) => {
                                                    if (reason === 'input') {
                                                        handleProductChange(item.id, newInputValue);
                                                    }
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        placeholder="Produkt wählen oder eingeben..."
                                                        error={highlightErrors && !item.productName.trim()}
                                                    />
                                                )}
                                                renderOption={(props, option) => (
                                                    <li {...props} key={option.id}>
                                                        <Box>
                                                            <Typography variant="body2">{option.name}</Typography>
                                                            {option.default_price && (
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {formatCurrency(option.default_price)}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </li>
                                                )}
                                                sx={{flexGrow: 1, minWidth: 200}}
                                            />
                                            <TextField
                                                size="small"
                                                label="Menge"
                                                value={item.quantity}
                                                onChange={(e) => handleUpdateLineItem(item.id, 'quantity', e.target.value)}
                                                sx={{width: 80}}
                                                inputProps={{style: {textAlign: 'right'}}}
                                                error={highlightErrors && parseNumber(item.quantity) <= 0}
                                            />
                                            <Select
                                                size="small"
                                                value={item.unitCode}
                                                onChange={(e) => handleUpdateLineItem(item.id, 'unitCode', e.target.value as string)}
                                                sx={{width: 80}}
                                            >
                                                {UNIT_OPTIONS.map(unit => (
                                                    <MenuItem key={unit.code} value={unit.code}>
                                                        {unit.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            <TextField
                                                size="small"
                                                label="Preis"
                                                value={item.unitPrice}
                                                onChange={(e) => handleUpdateLineItem(item.id, 'unitPrice', e.target.value)}
                                                sx={{width: 100}}
                                                inputProps={{style: {textAlign: 'right'}}}
                                                placeholder="0,00"
                                                error={highlightErrors && parseNumber(item.unitPrice) <= 0}
                                            />
                                            <TextField
                                                size="small"
                                                label="MwSt %"
                                                value={item.vatRate}
                                                onChange={(e) => handleUpdateLineItem(item.id, 'vatRate', e.target.value)}
                                                sx={{width: 70}}
                                                inputProps={{style: {textAlign: 'right'}}}
                                            />
                                            <Box sx={{textAlign: 'right', minWidth: 90}}>
                                                <Typography variant="body2" fontWeight="medium">
                                                    {formatCurrency(calculateLineTotal(item))}
                                                </Typography>
                                            </Box>
                                            <Tooltip title="Entfernen">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleRemoveLineItem(item.id)}
                                                    color="error"
                                                >
                                                    <DeleteIcon fontSize="small"/>
                                                </IconButton>
                                            </Tooltip>
                                        </Box>

                                        {/* Mobile: Stacked form fields */}
                                        <Box sx={{
                                            display: {xs: 'flex', md: 'none'},
                                            flexDirection: 'column',
                                            gap: 1.5,
                                            mb: 1,
                                        }}>
                                            <Autocomplete
                                                freeSolo
                                                size="small"
                                                options={products}
                                                getOptionLabel={(option) =>
                                                    typeof option === 'string' ? option : option.name
                                                }
                                                value={item.productId ? products.find(p => p.id === item.productId) || null : item.productName}
                                                onChange={(_, newValue) => handleProductChange(item.id, newValue)}
                                                onInputChange={(_, newInputValue, reason) => {
                                                    if (reason === 'input') {
                                                        handleProductChange(item.id, newInputValue);
                                                    }
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label="Produkt"
                                                        placeholder="Produkt wählen oder eingeben..."
                                                        error={highlightErrors && !item.productName.trim()}
                                                    />
                                                )}
                                                renderOption={(props, option) => (
                                                    <li {...props} key={option.id}>
                                                        <Box>
                                                            <Typography variant="body2">{option.name}</Typography>
                                                            {option.default_price && (
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {formatCurrency(option.default_price)}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </li>
                                                )}
                                                fullWidth
                                            />
                                            {/* Description directly under product on mobile */}
                                            <TextField
                                                size="small"
                                                value={item.description}
                                                onChange={(e) => handleUpdateLineItem(item.id, 'description', e.target.value)}
                                                multiline
                                                rows={2}
                                                placeholder="Beschreibung (optional)"
                                                label="Beschreibung"
                                                fullWidth
                                            />
                                            <Box sx={{display: 'flex', gap: 1}}>
                                                <TextField
                                                    size="small"
                                                    label="Menge"
                                                    value={item.quantity}
                                                    onChange={(e) => handleUpdateLineItem(item.id, 'quantity', e.target.value)}
                                                    sx={{flex: 1}}
                                                    inputProps={{style: {textAlign: 'right'}}}
                                                    error={highlightErrors && parseNumber(item.quantity) <= 0}
                                                />
                                                <Select
                                                    size="small"
                                                    value={item.unitCode}
                                                    onChange={(e) => handleUpdateLineItem(item.id, 'unitCode', e.target.value as string)}
                                                    sx={{flex: 1}}
                                                >
                                                    {UNIT_OPTIONS.map(unit => (
                                                        <MenuItem key={unit.code} value={unit.code}>
                                                            {unit.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </Box>
                                            <Box sx={{display: 'flex', gap: 1}}>
                                                <TextField
                                                    size="small"
                                                    label="Preis"
                                                    value={item.unitPrice}
                                                    onChange={(e) => handleUpdateLineItem(item.id, 'unitPrice', e.target.value)}
                                                    sx={{flex: 1}}
                                                    inputProps={{style: {textAlign: 'right'}}}
                                                    placeholder="0,00"
                                                    error={highlightErrors && parseNumber(item.unitPrice) <= 0}
                                                />
                                                <TextField
                                                    size="small"
                                                    label="MwSt %"
                                                    value={item.vatRate}
                                                    onChange={(e) => handleUpdateLineItem(item.id, 'vatRate', e.target.value)}
                                                    sx={{flex: 1}}
                                                    inputProps={{style: {textAlign: 'right'}}}
                                                />
                                            </Box>
                                        </Box>

                                        {/* Desktop: Description textarea */}
                                        <TextField
                                            size="small"
                                            value={item.description}
                                            onChange={(e) => handleUpdateLineItem(item.id, 'description', e.target.value)}
                                            multiline
                                            rows={2}
                                            placeholder="Beschreibung (optional)"
                                            label="Beschreibung"
                                            sx={{
                                                display: {xs: 'none', md: 'flex'},
                                                ml: 4,
                                                width: '75%',
                                            }}
                                        />
                                    </Box>
                                </Paper>
                            ))}
                        </Box>
                    )}

                    {/* Totals */}
                    {lineItems.length > 0 && (
                        <Box sx={{display: 'flex', justifyContent: 'flex-end', mb: 3}}>
                            <Box sx={{minWidth: 250}}>
                                <Box sx={{display: 'flex', justifyContent: 'space-between', mb: 0.5}}>
                                    <Typography variant="body2">Zwischensumme:</Typography>
                                    <Typography variant="body2">{formatCurrency(subtotal)}</Typography>
                                </Box>
                                {Object.entries(vatGroups).map(([rate, amount]) => (
                                    <Box key={rate} sx={{display: 'flex', justifyContent: 'space-between', mb: 0.5}}>
                                        <Typography variant="body2">zzgl. {rate}% MwSt.:</Typography>
                                        <Typography variant="body2">
                                            {formatCurrency(amount * parseFloat(rate) / 100)}
                                        </Typography>
                                    </Box>
                                ))}
                                <Divider sx={{my: 1}}/>
                                <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
                                    <Typography variant="body1" fontWeight="bold">Gesamt:</Typography>
                                    <Typography variant="body1" fontWeight="bold">
                                        {formatCurrency(total)}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    )}

                    <Divider sx={{my: 2}}/>

                    {/* Note Section */}
                    <Typography variant="subtitle2" color="text.secondary" sx={{mb: 1}}>
                        BEMERKUNG
                    </Typography>
                    <TextField
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        fullWidth
                        multiline
                        rows={2}
                        placeholder="Optionale Bemerkungen zur Rechnung..."
                    />

                    <Divider sx={{my: 2}}/>

                    {/* Payment Terms / Skonto Section */}
                    <Typography variant="subtitle2" color="text.secondary" sx={{mb: 1}}>
                        ZAHLUNGSBEDINGUNGEN
                    </Typography>
                    <Box sx={{display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap'}}>
                        <TextField
                            label="Zahlungsziel"
                            value={paymentDays}
                            onChange={(e) => setPaymentDays(e.target.value)}
                            size="small"
                            sx={{width: 120}}
                            type="number"
                            InputProps={{
                                endAdornment: <Typography variant="body2" color="text.secondary">Tage</Typography>,
                                inputProps: {min: 1},
                            }}
                        />
                        <TextField
                            label="Skonto %"
                            value={skontoPercentage}
                            onChange={(e) => setSkontoPercentage(e.target.value)}
                            size="small"
                            sx={{width: 120}}
                            placeholder="z.B. 3"
                            InputProps={{
                                endAdornment: <Typography variant="body2" color="text.secondary">%</Typography>,
                            }}
                        />
                        <TextField
                            label="Skonto Tage"
                            value={skontoDays}
                            onChange={(e) => setSkontoDays(e.target.value)}
                            size="small"
                            sx={{width: 120}}
                            placeholder="z.B. 14"
                            type="number"
                            InputProps={{
                                inputProps: {min: 1},
                            }}
                        />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{mt: 1}}>
                        {skontoPercentage && skontoDays
                            ? `Zahlungsbedingungen: ${skontoPercentage} % Skonto bei Zahlung innerhalb von ${skontoDays} Tagen; netto ohne Abzug innerhalb von ${paymentDays} Tagen.`
                            : `Zahlungsbedingungen: Zahlbar innerhalb von ${paymentDays} Tagen ohne Abzug.`}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} disabled={saving || finalizing}>
                        Abbrechen
                    </Button>
                    <Button
                        onClick={handleSaveDraft}
                        variant="outlined"
                        disabled={saving || finalizing}
                    >
                        {saving ? 'Speichern...' : 'Entwurf speichern'}
                    </Button>
                    <Button
                        onClick={handlePreviewPdf}
                        variant="outlined"
                        disabled={saving || finalizing}
                    >
                        PDF Vorschau
                    </Button>
                    <Tooltip
                        title={
                            !canFinalize().valid
                                ? 'Bitte füllen Sie alle Pflichtfelder aus (Kundenname, Adresse, mindestens eine Position)'
                                : ''
                        }
                        arrow
                    >
                        <Box
                            component="span"
                            sx={{
                                cursor: !canFinalize().valid ? 'not-allowed' : 'default',
                            }}
                            onMouseEnter={() => !canFinalize().valid && setHighlightErrors(true)}
                        >
                            <Button
                                onClick={handleFinalize}
                                variant="contained"
                                color="primary"
                                disabled={saving || finalizing || !canFinalize().valid}
                                sx={{
                                    pointerEvents: !canFinalize().valid ? 'none' : 'auto',
                                }}
                            >
                                {finalizing ? 'Abschließen...' : 'Abschließen'}
                            </Button>
                        </Box>
                    </Tooltip>
                </DialogActions>
            </Dialog>

            {/* Finalize Confirmation Dialog */}
            <Dialog
                open={showFinalizeConfirm}
                onClose={() => setShowFinalizeConfirm(false)}
            >
                <DialogTitle>Rechnung abschließen?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Nach dem Abschließen kann die Rechnung nicht mehr bearbeitet werden.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowFinalizeConfirm(false)}>
                        Abbrechen
                    </Button>
                    <Button onClick={confirmFinalize} variant="contained" color="primary">
                        Ja, abschließen
                    </Button>
                </DialogActions>
            </Dialog>
        </LocalizationProvider>
    );
};

export default InvoiceCreateDialog;
